import { Component, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Crumb, Breadcrumb, ComponentsCrumbs } from './header.interface';
import { Subscription, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { SalesDocumentService, PriceVersionService, getDocPositions, B2bDocumentPricesService } from '@icc/b2b/sales-document';
import { ConfigService, UserBoxData } from '@icc/shared';
import { NgxPermissionsService } from 'ngx-permissions';
import { Store } from '@ngrx/store';
import { ClockService, filterConsents, State } from '@icc/b2b/shared';
import { Panel } from '@icc/shared/types';
import { BreakpointObserver } from '@angular/cdk/layout';
import { _ } from '@icc/shared/helpers';

declare global {
    interface Window {
        toggleMenu?: () => void;
    }
}

@Component({
    selector: 'icc-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnDestroy {
	@Input() userBoxData: UserBoxData;
    @Input() activePanel: Panel | null = null;
    @Input() hasExternalMenu = false;
    @Input() condensedUserMenu = false;
    componentsCrumbs: ComponentsCrumbs = {};
    crumbsSubscriptions: Subscription[]  = [];
    breadcrumb: Breadcrumb = [];
    routerEventSubscription!: Subscription;
    crumbsSubject: Subject<any>;
    subscriptions: Subscription[] = [];
    project$ = combineLatest([
        this.b2bDocumentPricesService.priceUpdatePending$,
        this.salesDocumentService.openedDocument$,
        this.permissionsService.permissions$,
    ]).pipe(
        switchMap(async ([, doc]) =>
            doc != null
                ? {
                      number: doc.documentNumber ? doc.documentNumber.join('') : doc.uuid,
                      id: doc.uuid,
                      canBeEdited: await this.salesDocumentService.canAddPositionToQuotation(doc),
                  }
                : null
        )
    );

    project: {
        id: string;
        number: string;
        canBeEdited: boolean;
    } | null = null;

    openedDocumentPositionsCounter$ = this.store.select(getDocPositions).pipe(
        map(positions => positions.length > 0 ? positions.filter(position => !position.isDeleted).length : 0),
    );

    isLinkToProductsListVisible: boolean;

    creating = false;

    canAdd = false;
    consent = null;
    consents = [];

    @Input()
	set activatedRouteRootChildren(activatedRouteRootChildren:ActivatedRoute[]){
		this.subscribeCrumbs(activatedRouteRootChildren);
        this.createBreadcrumb();
	}
    @Input()themeLogo!:string;
    @Input()themeSmallLogo!:string;
    @Output()index = new EventEmitter<number|null>();

    hasAccessTo = {
        salesHeader: false,
        ordersList: false,
        unplacedOrdersList: false,
        purchaseInquiriesList: false,
        saleInquiriesList: false,
        productsList: false,
        pages: false,
    };

    maxLength = (name:string,length = 60):string => name.length > length ? name.substring(0, length-3) + '...' : name;

    get loaded() {
        return ClockService.getClock()?.timestamp != null;
    }

    isSublicense = this.configService.user?.user?.ActiveCompanyLicenseType === 'sublicense';

    menuOpened = window.innerWidth > 640 ? true : false;

    constructor(
        private store: Store<State>,
        private salesDocumentService: SalesDocumentService,
        private b2bDocumentPricesService: B2bDocumentPricesService,
        private priceVersionService: PriceVersionService,
		private router: Router,
        private priceVersionsService: PriceVersionService,
        private configService: ConfigService,
        private permissionsService: NgxPermissionsService,
        private breakpointObserver: BreakpointObserver,
        private http: HttpClient,
	) {
        this.isLinkToProductsListVisible = this.configService.config.config.Trade.tradePanelType === 'tiles';
        this.subscriptions.push(
            this.priceVersionService.activatedPriceVersion$.subscribe(async (priceVersion) => {
                if (this.userBoxData) {
                    this.userBoxData.activePriceVersionId = priceVersion?.id;
                }
            }),
            this.priceVersionService.actualPriceVersion$.subscribe(async (priceVersion) => {
                if (this.userBoxData) {
                    this.userBoxData.actualPriceVersionId = priceVersion?.id;
                }
            }),
            this.project$.subscribe((project) => {
                this.project = project;
                this.creating = false;
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.setAccess();
            }),
        );
        this.setAccess();
        this.checkPagesAvailability();
        if (this.configService.user.consent?.img) {
            this.consent = this.configService.user.consent;
        }
    }

    isDisabledActions() {
        return (
            this.configService.user?.user?.ActiveCompanyLicenseType === 'sublicense' &&
            this.configService.user?.consents?.length &&
            filterConsents(
                this.configService.user.consents,
                this.configService.user.acceptedConsents
            ).length
        );
    }

    async setAccess() {
        this.hasAccessTo.ordersList = this.configService.user.user.ActiveCompanyLicenseType !== 'license' &&
            (await this.permissionsService.hasPermission('purchase-make-order')) &&
            (await this.permissionsService.hasPermission(['purchase-own-orders-list', 'purchase-all-orders-list', 'purchase-resource-all-orders-list']));
        this.hasAccessTo.purchaseInquiriesList = this.configService.user.user.ActiveCompanyLicenseType !== 'license' &&
            (await this.permissionsService.hasPermission('purchase-inquiries-manage'));
        this.hasAccessTo.saleInquiriesList = this.configService.user.user.ActiveCompanyLicenseType === 'license' &&
            (await this.permissionsService.hasPermission(['sell-own-quotations', 'sell-all-quotations-list', 'sell-resource-all-quotations-list']));
        this.hasAccessTo.unplacedOrdersList = this.configService.user.user.ActiveCompanyLicenseType !== 'license' &&
            (await this.permissionsService.hasPermission('purchase-make-order')) &&
            (await this.permissionsService.hasPermission(['purchase-own-orders-list', 'purchase-all-orders-list', 'purchase-resource-all-orders-list']));

        this.hasAccessTo.productsList = this.isLinkToProductsListVisible && (await this.permissionsService.hasPermission('product-list'));

        this.hasAccessTo.salesHeader = this.hasAccessTo.ordersList ||
            this.hasAccessTo.purchaseInquiriesList ||
            this.hasAccessTo.saleInquiriesList ||
            this.hasAccessTo.unplacedOrdersList ||
            this.hasAccessTo.productsList ||
            (await this.permissionsService.hasPermission([
                'sell-own-orders-list',
                'sell-all-orders-list',
                'sell-resource-all-orders-list',
                'sell-own-quotations',
                'sell-all-quotations-list',
                'sell-resource-all-quotations-list',
            ])) ||
            this.hasPermissionsForOwnProducts();

        this.canAdd = await this.permissionsService.hasPermission('new-offer');
    }

    ngOnDestroy() {
        if(this.routerEventSubscription){
            this.routerEventSubscription.unsubscribe();
        }
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.unsubscribeCrumbs();
    }

    goHome() {
        this.router
            .navigateByUrl('/', { skipLocationChange: false })
            .then(() => this.router.navigate(['trade']));
    }

    onPriceVersionChange(id){
        this.priceVersionsService.activatePriceVersionData(id);
    }

    private unsubscribeCrumbs(){
        if(this.crumbsSubscriptions.length >0){
            this.crumbsSubscriptions.forEach(sub => sub.unsubscribe());
            this.crumbsSubscriptions = [];
        }
    }


    private subscribeCrumbs(
        routeChildren: ActivatedRoute[],
    ) {
        if(routeChildren?.length){
            for (const child of routeChildren) {
                if (typeof child.snapshot?.component !== 'string' && child.snapshot?.component) {
                    const crumbsSubject = (child.snapshot?.component as any).crumbsSubject;
                    if(crumbsSubject){
                        this.crumbsSubscriptions.push(crumbsSubject.subscribe(crumbSubject => {
                            if(crumbSubject?.action !== 'add'){
                                this.clearUpLevels(crumbSubject.level);
                            }
                            if(crumbSubject?.crumbs && crumbSubject.crumbs.length){
                                this.componentsCrumbs[crumbSubject.level] = crumbSubject.crumbs;
                            }
                            this.createBreadcrumb();
                        }));
                    }
                    const setCrumbs = (child.snapshot?.component as any).setCrumbs;
                    if(setCrumbs)setCrumbs();
                }
                this.subscribeCrumbs(child.children);
            }
        }
    }

    private clearUpLevels(level:number){
        const levels = Object.keys(this.componentsCrumbs);
        if(levels.length){
            for (const l of levels) {
                if(+l >= level){
                    delete this.componentsCrumbs[l];
                }
            }
        }
    }

    private createBreadcrumb(
    ) {

        this.breadcrumb = [ {
            label: _('PANEL|Panel handlowy'),
            url: '/trade',
            translate: true,
        }];
        for (const component in this.componentsCrumbs) {
            if (this.componentsCrumbs[component] && this.componentsCrumbs[component]?.length > 0) {
                for (const crumb of this.componentsCrumbs[component]) {
                    if(crumb){
                        this.breadcrumb.push(crumb);
                    }
                }
            }
        }
        this.handleBreadcrumbLength();
    }

    private handleBreadcrumbLength(){
        if(this.breadcrumb.length > 3 && this.breakpointObserver.isMatched('(min-width: 990px)')){
            const breadcrumb = [];
            breadcrumb.push(this.breadcrumb[0]);
            const crumb = this.breadcrumb.slice(-3)[0];
            breadcrumb.push({
                label: '...',
                url:crumb.url
            });
            this.breadcrumb = breadcrumb.concat(this.breadcrumb.slice(-2));
        } else if (this.breadcrumb.length > 2 && this.breakpointObserver.isMatched('(max-width: 990px)')) {
            const breadcrumb = [];
            breadcrumb.push(this.breadcrumb[0]);
            const crumb = this.breadcrumb.slice(-2)[0];
            breadcrumb.push({
                label: '...',
                url:crumb.url
            });
            this.breadcrumb = breadcrumb.concat(this.breadcrumb.slice(-1));
        }
    }

    isLastCrumb(crumb:Crumb){
        if(this.breadcrumb?.length){
            return crumb.url === this.breadcrumb[this.breadcrumb.length - 1].url && crumb.label === this.breadcrumb[this.breadcrumb.length - 1].label;
        }
        return false;
    }

    redirectTo(uri: string, origin: string) {
        this.router
            .navigateByUrl('/', { skipLocationChange: false })
            .then(() => this.router.navigate([uri], { queryParams: { origin: origin } }));
    }

    openCart() {
        if (this.project?.canBeEdited) {
            this.router.navigate(['trade', 'offer', this.project.id]);
        } else {
            this.createOffer();
        }
    }

    createOffer() {
        if (!this.creating) {
            this.creating = true;
            firstValueFrom(this.priceVersionService.loaded$).then(() => {
                const newDocument = this.salesDocumentService.createAndSaveDocument();
                this.salesDocumentService.openDocument(newDocument);
                this.router.navigate(['trade', 'offer', newDocument.uuid]);
            });
        }
    }

    hasPermissionsForOwnProducts(): boolean{
        const allPermissions = this.configService.config.userData?.user?.AllPermissions;
        const activeCompanyId = + this.configService.config.userData?.user?.ActiveCompany;
        const icAdminPermissions = allPermissions && activeCompanyId && allPermissions?.icadmin && allPermissions?.icadmin[activeCompanyId];
        if(icAdminPermissions && Object.values(icAdminPermissions).length){
            return (
                Object.values(icAdminPermissions).includes('icadmin-own-products')
                || Object.values(icAdminPermissions).includes('icadmin-own-products-limited')
            );
        }
        return false;
    }

    reloadB2bComplaintForm(path: string) {
        this.router.navigateByUrl('/trade', { skipLocationChange: true }).then(() => {
            this.router.navigate([path]);
        });
    }

    private checkPagesAvailability() {
        this.http.get<{ count: number }>(
            `${window.location.origin}/iccadmin/trade/user-interface/pages.json`,
            { params: { count_only: '1' } }
        ).pipe(take(1)).subscribe({
            next: (res) => {
                this.hasAccessTo.pages = res?.count > 0;
            },
            error: () => {
                this.hasAccessTo.pages = false;
            },
        });
    }

    toggleExternalMenu() {
        this.menuOpened = !this.menuOpened;
        if (window.toggleMenu) {
            window.toggleMenu();
        }
    }
}
